import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Repeat, Plus, Trash2, Pause, Play, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, EmptyState, Spinner, VerifiedBadge } from "../components/common";

export default function Recurring() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ cash_tag: "", amount: "", frequency: "monthly", note: "" });
  const [recipient, setRecipient] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/recurring"); setItems(r.data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const lookup = async (tag) => {
    setRecipient(null);
    const clean = tag.trim();
    if (clean.length < 2) return;
    try {
      const { data } = await api.get(`/users/search?cash_tag=${encodeURIComponent(clean)}`);
      if (!data.is_self) setRecipient(data);
    } catch { setRecipient(null); }
  };

  const create = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!recipient) { toast.error("Enter a valid Cash Tag"); return; }
    if (!amount || amount <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post("/recurring", { cash_tag: form.cash_tag, amount, frequency: form.frequency, note: form.note || null });
      toast.success("Recurring payment scheduled");
      setForm({ cash_tag: "", amount: "", frequency: "monthly", note: "" });
      setRecipient(null); setCreating(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (id) => {
    try { await api.post(`/recurring/${id}/toggle`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Cancel this recurring payment?")) return;
    try { await api.delete(`/recurring/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

  if (loading) return <FullLoader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Recurring" subtitle="Auto-send money on a schedule"
        action={<button data-testid="new-recurring-btn" onClick={() => setCreating(!creating)}
          className="grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground transition-transform active:scale-90"><Plus size={20} /></button>} />

      {creating && (
        <form onSubmit={create} data-testid="new-recurring-form" className="rounded-3xl bg-card border border-border p-5 mb-6 space-y-3">
          <div>
            <input placeholder="Recipient Cash Tag (e.g. $jack)" value={form.cash_tag} data-testid="recurring-cashtag-input"
              onChange={(e) => { setForm({ ...form, cash_tag: e.target.value }); lookup(e.target.value); }}
              className="w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm font-mono" />
            {recipient && (
              <div data-testid="recurring-recipient" className="flex items-center gap-1.5 mt-2 text-sm text-success font-medium">
                {recipient.display_name} {recipient.badge_active && <VerifiedBadge color={recipient.badge_color} size={14} />}
                <span className="text-muted-foreground font-mono">{recipient.cash_tag}</span>
              </div>
            )}
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <input type="number" min="0" step="0.01" placeholder="Amount" value={form.amount} data-testid="recurring-amount-input"
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full h-12 pl-8 pr-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["weekly", "monthly"].map((f) => (
              <button type="button" key={f} data-testid={`recurring-freq-${f}`} onClick={() => setForm({ ...form, frequency: f })}
                className={`h-12 rounded-xl font-semibold text-sm capitalize transition-colors ${form.frequency === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{f}</button>
            ))}
          </div>
          <input placeholder="Note (optional)" value={form.note} maxLength={140} data-testid="recurring-note-input"
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm" />
          <button type="submit" disabled={busy} data-testid="recurring-create-submit"
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : "Schedule payment"}
          </button>
        </form>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Repeat} title="No recurring payments"
          body="Schedule a weekly or monthly payment and we'll send it automatically.">
          <button data-testid="empty-new-recurring" onClick={() => setCreating(true)}
            className="h-12 px-6 rounded-xl bg-primary text-primary-foreground font-semibold">Schedule a payment</button>
        </EmptyState>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.schedule_id} data-testid={`recurring-${s.schedule_id}`} className="rounded-2xl bg-card border border-border p-4 flex items-center gap-3">
              <span className={`grid place-items-center w-11 h-11 rounded-full shrink-0 ${s.active ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}><Repeat size={19} /></span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{s.amount_display} to {s.recipient_name}</div>
                <div className="text-xs text-muted-foreground truncate capitalize">
                  {s.frequency} · {s.active ? `Next ${fmtDate(s.next_run)}` : "Paused"}
                </div>
              </div>
              <button data-testid={`recurring-toggle-${s.schedule_id}`} onClick={() => toggle(s.schedule_id)}
                className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-primary shrink-0">
                {s.active ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button data-testid={`recurring-delete-${s.schedule_id}`} onClick={() => del(s.schedule_id)}
                className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-muted-foreground hover:text-destructive shrink-0">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
